/**
 * Admin users list — view/filter/change roles.
 */
import { useEffect, useState } from "react";
import { Mast } from "../components/Mast.js";
import { api } from "../lib/api.js";

interface User {
  id: string; email: string; role: string;
  display_name: string | null;
  approved_at: string | null;
  created_at: string;
}

const ROLES = ["all", "trainer_pending", "trainer", "case", "admin"] as const;

export default function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<typeof ROLES[number]>("all");

  async function load() {
    setLoading(true);
    const r = await api<{ ok: true; users: User[] }>(
      `/api/admin/users${filter === "all" ? "" : `?role=${filter}`}`,
    );
    setUsers(r.users);
    setLoading(false);
  }
  useEffect(() => { load(); }, [filter]);

  async function changeRole(id: string, role: string) {
    if (!confirm(`真的要把這個 user 改為 ${role}？`)) return;
    try {
      await api(`/api/admin/users/${id}/role`, { method: "POST", body: { role } });
      await load();
    } catch (e: any) {
      alert(`Failed: ${e?.body?.error ?? e?.message}`);
    }
  }

  return (
    <>
      <Mast section="Admin · users" />
      <main className="page">
        <section style={{ marginBottom: "var(--s-7)" }}>
          <div className="serial rise rise-1">USERS · {users.length}</div>
          <h1 className="rise rise-2"><span className="italic-display">使用者</span> 列表</h1>
          <p className="muted rise rise-3" style={{ marginTop: "var(--s-3)", fontSize: "var(--t-sm)" }}>
            可改 role；trainer_pending → trainer 通過審核（也可從 Approvals 頁做）。
          </p>
        </section>

        <div className="row rise rise-4" style={{ marginBottom: "var(--s-5)", gap: "var(--s-3)", flexWrap: "wrap" }}>
          {ROLES.map(r => (
            <button
              key={r}
              onClick={() => setFilter(r)}
              className={filter === r ? "btn btn--small" : "btn btn--small btn--ghost"}
            >
              {r}
            </button>
          ))}
        </div>

        <section className="rise rise-5">
          {loading ? (
            <div className="muted">載入中…</div>
          ) : users.length === 0 ? (
            <div className="empty muted">沒有使用者符合篩選</div>
          ) : (
            <div className="list">
              <div className="list-row" style={{
                gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 2fr) minmax(0, 1fr) auto",
                borderBottom: "1px solid var(--ink)",
                paddingBottom: "var(--s-2)",
                marginBottom: "var(--s-1)",
              }}>
                <div className="serial">姓名</div>
                <div className="serial">Email</div>
                <div className="serial">建立日</div>
                <div className="serial" style={{ textAlign: "right" }}>Role · 動作</div>
              </div>
              {users.map(u => (
                <div key={u.id} className="list-row" style={{ gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 2fr) minmax(0, 1fr) auto" }}>
                  <div>
                    <div className="list-row__name">{u.display_name ?? "—"}</div>
                    <div className="list-row__meta">{u.id.slice(0, 12)}</div>
                  </div>
                  <div style={{ fontSize: "var(--t-sm)", overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>
                  <div className="muted tabular">{u.created_at.slice(0, 10)}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: "var(--s-2)" }}>
                    <RoleChip role={u.role} />
                    <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)}
                            style={{ fontSize: "var(--t-xs)", padding: "4px 6px", width: "auto" }}>
                      <option value="trainer_pending">pending</option>
                      <option value="trainer">trainer</option>
                      <option value="case">case</option>
                      <option value="admin">admin</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </>
  );
}

function RoleChip({ role }: { role: string }) {
  if (role === "admin")           return <span className="chip chip--rust">admin</span>;
  if (role === "trainer")         return <span className="chip chip--olive">trainer</span>;
  if (role === "trainer_pending") return <span className="chip chip--warning">pending</span>;
  if (role === "case")            return <span className="chip chip--petrol">case</span>;
  return <span className="chip">{role}</span>;
}
